import { Component, OnInit, Input, EventEmitter, Output, ElementRef, ViewChild, OnChanges, signal, computed } from '@angular/core';
import { FlowObjectDefinition, FlowObjectInstance, FlowObjectInstanceState, FlowObjectType } from '../../../models/flow-object.model';
import { ToastrService } from 'ngx-toastr';
import { Enums } from '../../../shared/enums';
import { AuthService } from '../../../services/auth.service';
import { EDocsService } from '../../../services/edocs.service';
import { Papel } from '../../../models/edocs.model';
import { Utils } from '../../../shared/utils';
import { FlowDefinition, FlowInstance, FlowTarget } from '../../../models/flow.model';
import { InputDataTaskForm } from '../../../models/input-output-data.model';
import { ConfigSchema } from '../../../models/config-schema.model';
import { FORMIO_OPTIONS } from '../form/formio/formio-options';
import { IMPORTS } from './_imports';

@Component({
    selector: 'fob-start-inbound-api',
    templateUrl: './fob-start-inbound-api.component.html',
    styleUrl: './fob-start-inbound-api.component.scss',
    standalone: true,
    imports: IMPORTS
})
export class FlowObjectStartInboundApiComponent implements OnInit, OnChanges {
    // #region [ViewChild]
    @ViewChild('papelRef') papelRef: ElementRef;
    // #endregion

    // #region [Type props]
    FlowObjectInstanceState = FlowObjectInstanceState;
    FlowObjectType = FlowObjectType;
    Utils = Utils;
    Enums = Enums;
    // #endregion

    // #region [props]
    configSchema: ConfigSchema;
    formioOptions: any = FORMIO_OPTIONS;
    fileUrls: any;
    // #endregion

    // #region [signals]
    $flowObjectInstance = signal(null as FlowObjectInstance);
    $flowObjectDefinition = signal(null as FlowObjectDefinition);
    $flowInstance = signal(null as FlowInstance);
    $flowDefinition = signal(null as FlowDefinition);
    $papeisSelector = signal([] as Papel[]);
    $inputData = signal(null as InputDataTaskForm);
    $formSchema = signal(null as any);
    $pdfFiles = signal([] as any[]);
    $publicMessageHtml = signal(null as string);
    // #endregion

    // #region [computeds]
    $currentUser = computed(() => this.authService.$user());
    $isPublicMessageHtmlLoading = computed(() => Utils.isNullOrEmpty(this.$publicMessageHtml()));
    $shouldShowPapelSelector = computed(() => this.$flowDefinition()?.targetId != FlowTarget.Citizen && this.$papeisSelector().length > 0);
    $hasPendingTask = computed(() => this.$flowObjectInstance()?.stateId == FlowObjectInstanceState.Started);
    $isReadOnly = computed(() => [FlowObjectInstanceState.Finished, FlowObjectInstanceState.NotStarted].includes(this.$flowObjectInstance()?.stateId));
    $dateString = computed(() => this.$flowObjectInstance() == null ? '' : new Date(this.$flowObjectInstance().updateDate).toLocaleString());
    $isTaskPublicAgentActor = computed(() =>
        this.$currentUser()?.isPublicAgent
        && this.$flowObjectInstance()?.flowObjectInstanceActors.some(x => x.actorId == this.$currentUser()?.id)
        && this.$flowInstance()?.ownerId != this.$currentUser()?.id
    );
    $isBootstrapFinished = computed(() =>
        this.$flowObjectInstance() != null
        && this.$flowObjectDefinition() != null
        && this.$flowInstance() != null
        && this.$flowDefinition() != null
    );
    // #endregion

    // #region [Input/Output]
    @Input() _flowObjectInstance: FlowObjectInstance;
    @Input() _flowObjectDefinition: FlowObjectDefinition;
    @Input() _flowInstance: FlowInstance;
    @Input() _flowDefinition: FlowDefinition;
    @Output() _submitEvent = new EventEmitter<FlowObjectInstance>();
    // #endregion

    constructor(
        private toastr: ToastrService,
        private authService: AuthService,
        private eDocsService: EDocsService
    ) {
        // #region [hook do Formio para interceptação do submit e eventual alteração dos dados submetidos]
        // utilizado aqui para o tratamento diferenciado necessário aos
        // componentes customizados de arquivo (i.e.POST no Minio)
        this.formioOptions.hooks.beforeSubmit = async (submission, next) => {
            // checa se há um papel selecionado antes de prosseguir
            if (Utils.isNullOrEmpty(this.$inputData().eDocsData.signerId)) {
                Utils.scrollToTop();
                this.toastr.error(Enums.Messages.NoSignerIdError, Enums.Messages.Error, Utils.getToastrErrorOptions());
                next([{ message: null }]);
                return;
            }

            // #region [tratamento de componentes do tipo "Arquivo PDF"]
            let submittedCustomFileComponentKeys = Object.keys(this.fileUrls);

            for (let key of submittedCustomFileComponentKeys) {
                let label = this.$formSchema().components.find(x => x.key == key).label;

                if (Array.isArray(this.fileUrls[key])) {
                    submission.data[key] = {
                        fileName: [],
                        label: [],
                        minioKey: [],
                        isCustomFileComponent: true
                    };

                    for (let item of this.fileUrls[key]) {
                        submission.data[key].fileName.push(`${label}.pdf`);
                        submission.data[key].label.push(label);
                        submission.data[key].minioKey.push(item);
                    }
                } else {
                    submission.data[key] = {
                        fileName: [`${label}.pdf`],
                        label: [label],
                        minioKey: [this.fileUrls[key]],
                        isCustomFileComponent: true
                    };
                }
            }

            if (
                // checa a validade da assinatura ICP-Brasil de anexos que a exijam, caso exista algum no form
                await Utils.isValidIcpBrasil(submission.data, this.$formSchema(), this.eDocsService, this.toastr)
                // checa se o anexo é capturável no E-Docs
                && await Utils.isValidEDocsDocument(submission.data, this.$formSchema(), this.eDocsService, this.toastr)
            ) {
                // prossegue com o lifecycle padrão do Formio
                next(null, submission);
            } else {
                next([{ message: null }]);
            }
            // #endregion
        };
        // #endregion
    }

    ngOnInit() {
        this.configSchema = JSON.parse(this.$flowObjectDefinition().configSchema) as ConfigSchema;
        this.$formSchema.set(JSON.parse(this.$flowObjectDefinition().formSchema));
        Utils.setSignal(this.$inputData, Object.assign(new InputDataTaskForm(), JSON.parse(this.$flowObjectInstance().inputData)));
        this.fileUrls = JSON.parse(this.$inputData().fileUrls);

        // #region [listagem de campos de anexos]
        let multipleFilesComponents = this.$formSchema().components.filter(x => x.type == 'pdfUploadMultiple').map(x => x.key);
        for (let key of Object.keys(this.fileUrls)) {
            if (multipleFilesComponents.includes(key)) {
                let count = this.fileUrls[key].length;
                for (let i = 1; i <= count; i++) {
                    Utils.updSignal(this.$pdfFiles, xx => {
                        xx.push({ filename: `${this.$formSchema().components.find(x => x.key == key).label} (${i}).pdf` });
                        return xx;
                    });
                }
            } else {
                Utils.updSignal(this.$pdfFiles, xx => {
                    xx.push({ filename: `${this.$formSchema().components.find(x => x.key == key).label}.pdf` });
                    return xx;
                });
            }
        }
        // #endregion

        Utils.updSignal(this.$formSchema, xx => {
            xx.components.filter(x => x.type != 'button').forEach(x => {
                x.hidden = true;
                x.validate.required = false;
            });
            return xx;
        });

        // #region [seletor de papéis]
        if (this.$flowDefinition()?.targetId != FlowTarget.Citizen) {
            Utils.setSignal(this.$papeisSelector, this.$currentUser().papeis);

            // se rota "/flow-definition"
            if (this.$flowObjectInstance() == null) {
                // força o usuário a selecionar um papel manualmente
                setTimeout(() => Utils.updSignal(this.$inputData, x => {
                    x.eDocsData.signerId = null;
                    return x;
                }), 50);
            }
        } else {
            Utils.updSignal(this.$inputData, x => {
                x.eDocsData.signerId = this.$currentUser().id;
                return x;
            });
        }
        // #endregion

        // #region [mensagem pública customizada]
        if (Utils.isNotNullOrEmpty(this.$flowObjectDefinition()?.publicMessageHtml)) {
            setTimeout(() => this.$publicMessageHtml.set(this.$flowObjectDefinition().publicMessageHtml), 1000);
        }
        // #endregion
    }

    ngOnChanges() {
        this.$flowInstance.set(this._flowInstance);
        this.$flowDefinition.set(this._flowDefinition);
        this.$flowObjectInstance.set(this._flowObjectInstance);
        this.$flowObjectDefinition.set(this._flowObjectDefinition);
    }

    signerIdChange(event) {
        Utils.updSignal(this.$inputData, x => {
            x.eDocsData.signerId = event;
            return x;
        });
    }

    onSubmit(event) {
        Utils.updSignal(this.$inputData, x => {
            x.data = event.data;
            return x;
        });
        Utils.updSignal(this.$flowObjectInstance, x => {
            x.inputData = JSON.stringify(this.$inputData());
            return x;
        });
        this._submitEvent.emit(this.$flowObjectInstance());
    }

    // #region [===== private methods =====]
    // #endregion
}
